/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @coauthor David Roman <david@inarix.com>
 * @file LicencesTable.tsx
 * @desc Created on Mon Apr 28 2025 20:44:52
 * @copyright All rights reserved @ Inarix
 */

import { FC, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Box, Modal, Snackbar, ThemeProvider } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { TFunction } from "i18next";

import themeInarix from "../StylesMuiInarix";
import { selectLocale } from "../../redux/slices/layout";
import {
  focusedLicense,
  licenseAssignation,
  clearLicenseNotification,
  selectLicenseLoading,
  selectLicenseNotification,
  selectModalLicense,
  setLicenseId,
  LicensesFilters,
  setShowFilterForm,
  setPreselectedFilterField,
  selectUnassignedCount,
  selectExpiredCount,
  selectTotalLicensesCount,
  setFilterModel,
  setOpenCells,
  selectUpcomingCount,
} from "../../redux/slices/licenses/licenseList";
import { LicensePacksModal } from "./LicensePacksModal";
import { useLicenseColumns } from "./LicenceColumns";
import LicenseAssignationModal from "./LicenseAssignationModal";
import {
  getDataGridLocale,
  managingFiltersText,
} from "../data/dataHelpers/DataProgress";
import CustomGeneralColumnHeader from "../customFilterComponent/customHeader";
import { GroupCell } from "./GroupCell";
import { LicenseQuickFilter } from "./LicenseQuickFilter";
import { FirstLetterUpperCase } from "../helpers/helpersSearchs";
import { LicenseStylesColums } from "./licenseStyles/LicenseColumns";
import { useLicenseTableOptions } from "./useLicenseTableOptions";
import {
  tableContainerSx,
  tableDescriptionStyle,
  alertSx,
} from "./licenseStyles/LicencesTable.styles";

type LicencesTableParams = {
  apiRef: ReturnType<typeof useGridApiRef>;
  initData: (dispatch: ThunkDispatch<any, any, any>) => void;
  t: TFunction<"global", undefined>;
};

const SetActions = (dispatch: ThunkDispatch<any, any, any>) => {
  return {
    setLicenseId: (id: string) => dispatch(setLicenseId(id)),
    focusedLicense: (id: string) => dispatch(focusedLicense(id)),
    setOpenCells: (open: boolean) => dispatch(setOpenCells(open)),
    onOpenFilter: (field: string) => {
      dispatch(setShowFilterForm(false));
      dispatch(setPreselectedFilterField(field));
      setTimeout(() => {
        dispatch(setShowFilterForm(true));
      }, 50);
    },
  };
};

export const LicencesTable: FC<LicencesTableParams> = ({
  apiRef,
  initData,
  t,
}) => {
  const dispatch = useDispatch();
  const locale = useSelector(selectLocale);
  const loading = useSelector(selectLicenseLoading);
  const modalLicense = useSelector(selectModalLicense);
  const { licenseId } = useSelector(licenseAssignation);
  const notification = useSelector(selectLicenseNotification);
  const { filterModel } = useSelector(LicensesFilters);
  const unassignedCount = useSelector(selectUnassignedCount) as number;
  const expiredCount = useSelector(selectExpiredCount) as number;
  const upcomingCount = useSelector(selectUpcomingCount) as number;
  const totalCount = useSelector(selectTotalLicensesCount) as number;
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const { tableOptions, expandedRows, setExpandedRows } =
    useLicenseTableOptions({ apiRef, t });
  const {
    licenseColumns,
    licensesRows,
    sortAlpha,
    toggleAlphaSort,
    activeSort,
  } = useLicenseColumns(t, dispatch, tableOptions);
  const stableRows = useMemo(() => licensesRows, [licensesRows]);
  const { setLicenseId, focusedLicense, onOpenFilter } = SetActions(dispatch);
  const [loadingAssignLicense, setLoadingAssignLicense] = useState(false);

  const activeCount = Math.max(
    totalCount - unassignedCount - expiredCount - upcomingCount,
    0,
  );

  const filterOptions = [
    {
      key: "all",
      label: FirstLetterUpperCase(t("licenses.all")),
      count: totalCount,
      color: LicenseStylesColums.statusColors.all.color,
      bg: LicenseStylesColums.statusColors.all.bg,
    },
    {
      key: "active",
      label: FirstLetterUpperCase(t("licenses.active")),
      count: activeCount > 0 ? activeCount : 0,
      color: LicenseStylesColums.statusColors.active.color,
      bg: LicenseStylesColums.statusColors.active.bg,
    },
    {
      key: "unassigned",
      label: t("licenses.unassigned"),
      count: unassignedCount,
      color: LicenseStylesColums.statusColors.unassigned.color,
      bg: LicenseStylesColums.statusColors.unassigned.bg,
    },
    {
      key: "upcoming",
      label: FirstLetterUpperCase(t("licenses.upcoming")),
      count: upcomingCount,
      color: LicenseStylesColums.statusColors.upcoming.color,
      bg: LicenseStylesColums.statusColors.upcoming.bg,
    },
    {
      key: "expired",
      label: FirstLetterUpperCase(t("licenses.expired")),
      count: expiredCount,
      color: LicenseStylesColums.statusColors.expired.color,
      bg: LicenseStylesColums.statusColors.expired.bg,
    },
  ];

  const optionsQuickFilter = () => (
    <LicenseQuickFilter
      filterOptions={filterOptions}
      activeFilter={activeFilter}
      onFilterChange={setActiveFilter}
      filterModel={filterModel}
      setFilterModels={(model) => dispatch(setFilterModel(model))}
      t={t}
    />
  );

  console.log(stableRows);

  return (
    <Box>
      <ThemeProvider theme={themeInarix(locale)}>
        <div className="container-licenses-table">
          <Box sx={tableContainerSx}>
            <div style={tableDescriptionStyle}>
              <Alert severity="info" sx={alertSx}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: `${t("licenses.tableDescription")}`,
                  }}
                />
              </Alert>
            </div>

            {optionsQuickFilter()}
            <DataGridPro
              apiRef={apiRef}
              rows={stableRows}
              columns={licenseColumns}
              rowHeight={70}
              pagination
              disableRowSelectionOnClick={true}
              disableColumnSelector={true}
              treeData
              getTreeDataPath={(row) => row.path}
              defaultGroupingExpansionDepth={0}
              density="compact"
              isGroupExpandedByDefault={(node) =>
                expandedRows.has(String(node.id))
              }
              groupingColDef={{
                headerName: t("Data.table.Username"),
                width: 260,
                renderHeader(params) {
                  return (
                    <CustomGeneralColumnHeader
                      headerName={t("Data.table.Username")}
                      field={"username"}
                      filterModel={filterModel}
                      availableFilters={["$ilike", "$eq", "$ne"]}
                      onOpenFilter={() => onOpenFilter("username")}
                      backgroundColor="#fafafa"
                      newOptions={tableOptions}
                      sortable={true}
                      sortModel={
                        activeSort === "username" && sortAlpha
                          ? [{ field: "username", sort: sortAlpha }]
                          : []
                      }
                      onSort={toggleAlphaSort}
                    />
                  );
                },
                renderCell: (params) => (
                  <GroupCell
                    params={params}
                    t={t}
                    onAssign={(id) => setLicenseId(id)}
                    onToggle={(id, expanded) => {
                      setExpandedRows((prev) => {
                        const next = new Set(prev);
                        expanded ? next.add(id) : next.delete(id);
                        return next;
                      });
                    }}
                  />
                ),
              }}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 25 },
                },
              }}
              loading={loading}
              pageSizeOptions={[25, 50, 100]}
              ignoreDiacritics={true}
              localeText={
                {
                  ...getDataGridLocale(locale),
                  noRowsLabel: managingFiltersText(
                    filterModel,
                    t,
                    licensesRows,
                  ),
                } as any
              }
            />
          </Box>
        </div>
        <Modal open={!!modalLicense} onClose={() => focusedLicense("")}>
          <LicensePacksModal />
        </Modal>

        <Modal
          open={!!licenseId}
          onClose={loadingAssignLicense ? undefined : () => setLicenseId("")}
        >
          <LicenseAssignationModal
            initData={initData}
            t={t}
            setLoadingAssignLicense={setLoadingAssignLicense}
            loadingAssignLicense={loadingAssignLicense}
          />
        </Modal>

        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={() => dispatch(clearLicenseNotification())}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => dispatch(clearLicenseNotification())}
            severity={notification.severity}
          >
            {notification.message ? t(`licenses.${notification.message}`) : ""}
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </Box>
  );
};
